import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import type { ActorRefFrom } from 'xstate';
import { assign, enqueueActions, fromPromise, setup } from 'xstate';

import { ApiRespCode, CheckPasswordStatus } from '@webapp/common/lib/api';
import { isAllPagesOnOnePage, isBrowser, isPreview } from '@webapp/common/lib/const';
import { clearSurveyTokenCookie, getSurveyPromocode, setSurveyTokenCookie } from '@webapp/common/lib/cookies';
import { createDuration, partsToSeconds } from '@webapp/common/lib/date';
import { LocalStorage } from '@webapp/common/lib/storage';
import { isEmbedded } from '@webapp/common/lib/ua';
import type { SurveyStore, TestScore, TestScoreAnswer } from '@webapp/common/resources/entities';
import { BundleFinishType, BundleType, initialSurveyStore } from '@webapp/common/resources/entities';
import type { InfoModel } from '@webapp/common/resources/entities/info';
import { createQuestion } from '@webapp/common/resources/entities/question';
import {
    LogicTransition,
    QuestionType,
    TestCheckStatus,
    TypesWithScore,
    WelcomePageType,
} from '@webapp/common/resources/survey';

import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { BundleEntity } from '@webapp/survey/src/entities/bundle';
import { QuestionMachineDefinition } from '@webapp/survey/src/machine/question-machine';
import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';
import { notifyPageChanged, scrollSurveyToTop } from '@webapp/survey/src/utils/scroll';

import {
    bootstrap,
    checkPwdAuth,
    checkVkAuth,
    disqualSurvey,
    fetchContent,
    fillAbTestingQuestions,
    finishSurvey,
    finishSurveyExpectedErrors,
    isFinishTypeResults,
} from './actions';
import { addDetails, setQuotaSessionActive, surveyApi } from './api';
import { AudioMachineDefinition } from './audio-machine';
import { bootstrapMachine } from './bootstrap-machine';
import type { SurveyEvent } from './events';
import { withAuth, withSurveyTimer } from './guards';
import { prepareBundles } from './helpers/bundles';
import { hideCarryForwardAnswers, mapQuestionForQueue, mapQuestionForSnapshot } from './helpers/question';
import {
    collectBundleQuotaClaims,
    collectResumeQuotaClaims,
    findQuotaHideRollback,
    findQuotaLogicById,
    hasQuotaHideLogic,
    hideQuotaAnswers,
    isQuotaHideLogic,
    resetTailQuestions,
    syncQuotaHides,
} from './helpers/quota';
import { calcSurveyProgress, getPipingAnswers } from './helpers/survey';
import {
    assertEventType,
    authenticated,
    clearTimersOfOtherResponse,
    fillQuestions,
    incIndividualTries,
    isQuestionTimerExpired,
    loadTimer,
    parseTokenData,
    removeTimer,
    saveIncompleteAnswersOfSurvey,
} from './lib';
import { QuotaMachineDefinition } from './quota-machine';
import { selectCurrentBundle, selectFirstInvalidId, selectNextBundle, selectSaveIncomplete } from './selectors';
import { SurveyState } from './states';
import { TimerMachine } from './timer-machine';
import { clearTtId, setTtId, tryToGetTtId } from './tt-id';

// TODO https://stately.ai/blog/introducing-typescript-typegen-for-xstate
// https://xstate.js.org/docs/guides/models.html#createmodel
// https://github.com/NoriSte/people-list-xstate/tree/main/
// https://www.youtube.com/watch?v=s-nqoIbIZac
// check https://github.com/statelyai/xstate/discussions/2221#discussioncomment-795137
// TODO use https://xstate.js.org/docs/packages/xstate-immer/

// TODO use named conditions for better viz

export const saveQuestionsSnapshot = (domain: string, questions: Array<QuestionActorRef>): void => {
    const questionsSnap = JSON.stringify(questions.map(mapQuestionForSnapshot));
    LocalStorage.set(`new_answers__${domain}`, questionsSnap);
};

const setToken = (token: string) => {
    if (token) {
        surveyApi.setToken(token);

        if (isBrowser) {
            setSurveyTokenCookie(token);
        }
    } else {
        surveyApi.setToken(null);

        if (isBrowser) {
            clearSurveyTokenCookie();
        }
    }
};
export const surveyMachine = setup({
    types: {
        context: {} as SurveyStore,
        events: {} as SurveyEvent,
    },
    actors: {
        bootstrapInvoke: fromPromise<
            {
                code: number;
                token: string;
                startDatetime: dayjs.Dayjs;
                info: InfoModel | null;
                agreementText: string | null;
                qp: Record<string, string>;
                quotaFilled: boolean;
                complete: boolean;
                promo_code: string;
            },
            { domain: string; token: string; update?: boolean; individualTries: number }
        >(async ({ input }) => {
            const data = input;
            const resp = await bootstrap(data);

            return resp;
        }),
        fetchContent,
        checkPwdAuth: fromPromise<
            { token: { code: number; token: string }; password: string },
            { individualPasswords: boolean; password: string }
        >(({ input }) => {
            return checkPwdAuth(input.individualPasswords, input.password);
        }),
        finishSurvey: fromPromise<
            { promo_code: string; testScore: TestScore | null; quotaDisqual: boolean },
            { context: SurveyStore }
        >(async ({ input: { context } }) => {
            let promo_code: string, testScore;
            let quotaDisqual = false;

            switch (context.stopReason) {
                case BundleFinishType.STOP:
                case BundleFinishType.STOP_BY_TIMER:
                case BundleFinishType.TEXT:
                    try {
                        ({ promo_code, testScore, quotaDisqual } = await finishSurvey(context));
                    } catch (e) {
                        throw e;
                    }
                    // clearSurveyTokenCookie();
                    break;
                case BundleFinishType.DISQUAL:
                    await disqualSurvey(context);

                    break;
            }

            return { promo_code, testScore, quotaDisqual };
        }),
    },
    actions: {
        // Прохождение завершено (stop/response/disqual), сервис квот закоммитил
        // брони: глушим heartbeat и больше не освобождаем слоты при закрытии.
        stopQuotaSession: ({ context }) => {
            context.quotaRef?.send({ type: 'STOP_QUOTAS' });
            setQuotaSessionActive(context.domain, false);
        },
    },
}).createMachine({
    id: 'SurveyMachine',
    initial: SurveyState.idle,
    schema: {
        context: {} as SurveyStore,
        events: {} as SurveyEvent,
    },
    context: cloneDeep(initialSurveyStore),
    states: {
        [SurveyState.idle]: {
            entry: assign({
                bootstrapRef: ({ spawn, self }) =>
                    spawn(bootstrapMachine, {
                        input: {
                            parentRef: self,
                        },
                    }),
            }),
            on: {
                BOOTSTRAP: {
                    target: SurveyState.bootstrap,
                    actions: assign(({ event }) => {
                        const { domain, individualTries, token } = event;
                        setToken(token);
                        return {
                            individualTries,
                            domain,
                            token,
                        };
                    }),
                },
            },
        },
        [SurveyState.bootstrap]: {
            invoke: {
                id: 'bootstrap',
                src: 'bootstrapInvoke',
                input: ({ context, event }) => {
                    assertEventType(event, ['BOOTSTRAP', 'RELOAD']);
                    const data = event.type === 'BOOTSTRAP' ? event : context;
                    return {
                        domain: data.domain,
                        token: data.token,
                        update: 'update' in data ? data.update : undefined,
                        individualTries: data.individualTries,
                    };
                },
                onDone: [
                    {
                        target: SurveyState.retryNotAllowed,
                        guard: ({
                            event: {
                                output: { code, promo_code },
                            },
                        }) => {
                            if (code === ApiRespCode.ERR_IP_UNIQ_CODE) {
                                return true;
                            }

                            // 70/77 — повторный заход (устройство/персональная
                            // ссылка). Если бэкенд вернул промокод для этого
                            // устройства или он уже сохранён в cookie, показываем
                            // страницу завершения с ним, а не заглушку
                            // «вы уже прошли» (#1026).
                            if (code === ApiRespCode.SURVEY_COMPLETE || code === ApiRespCode.SURVEY_FILLED) {
                                return !(promo_code || getSurveyPromocode());
                            }

                            return false;
                        },
                        actions: assign(({ event }) => {
                            return { info: event.output.info, agreementText: event.output.agreementText };
                        }),
                    },
                    {
                        // Общая квота опроса набрана, но опрос НЕ отключён:
                        // показываем страницу завершения с текстом quotaFilledText
                        // (если квоту увеличить — следующий старт снова пустит).
                        target: SurveyState.completed,
                        guard: ({
                            event: {
                                output: { quotaFilled },
                            },
                        }) => Boolean(quotaFilled),
                        actions: assign(({ event }) => {
                            return {
                                info: event.output.info,
                                agreementText: event.output.agreementText,
                                quotaFilled: true,
                            };
                        }),
                    },
                    {
                        target: SurveyState.disabled,
                        guard: ({
                            event: {
                                output: { code },
                            },
                        }) => {
                            return code === ApiRespCode.ERR_SVR_CODE;
                        },
                        actions: assign(({ event }) => {
                            return { info: event.output.info, agreementText: event.output.agreementText };
                        }),
                    },
                    {
                        target: SurveyState.completed,
                        guard: ({
                            event: {
                                output: { code, complete },
                            },
                        }) =>
                            Boolean(complete) ||
                            code === ApiRespCode.SURVEY_COMPLETE ||
                            code === ApiRespCode.SURVEY_FILLED,
                        actions: assign(
                            ({
                                context,
                                event: {
                                    output: { info, token, agreementText, promo_code },
                                },
                            }) => {
                                const tokenData = parseTokenData(token);

                                setToken(token);

                                const surveyPromocode = getSurveyPromocode();
                                const questionAnswers: Record<string, any> =
                                    LocalStorage.get(`survey_${context.domain}_question_answers`) || {};
                                const linkParamsSaved: Record<string, any> =
                                    LocalStorage.get(`survey_${context.domain}_link_params`) || {};

                                return {
                                    info,
                                    token,
                                    questionAnswers,
                                    linkParams: { ...context.linkParams, ...linkParamsSaved },
                                    tokenData,
                                    agreementText,
                                    // Сначала промокод из ответа бэкенда (перезагрузка
                                    // с токеном), затем сохранённый в cookie (повторный
                                    // заход без токена). Не подставляем "null"/"undefined".
                                    promocode: promo_code || surveyPromocode || '',
                                };
                            },
                        ),
                    },
                    {
                        target: SurveyState.auth,
                        guard: ({
                            event: {
                                output: { code },
                            },
                        }) => code === ApiRespCode.ERR_AUTH_CODE,
                        actions: assign(
                            ({
                                event: {
                                    output: { qp },
                                },
                            }) => ({ queryParams: qp }),
                        ),
                    },
                    {
                        target: SurveyState.auth,
                        guard: ({
                            event: {
                                output: { info, token },
                            },
                        }) => {
                            const { passwordAccess, vkAuth } = info.params.main;
                            const authOn = passwordAccess || vkAuth;
                            const tokenData = parseTokenData(token);
                            return authOn && !authenticated({ tokenData });
                        },
                        actions: enqueueActions(
                            ({
                                event: {
                                    output: { info, token, startDatetime, agreementText, qp },
                                },
                                enqueue,
                            }) => {
                                const tokenData = parseTokenData(token);

                                setToken(token);

                                enqueue.assign({
                                    agreementText,
                                    loading: ({ context }) => ({ ...context.loading, details: Boolean(info.details) }),
                                    info,
                                    token,
                                    tokenData,
                                    startDatetime,
                                    queryParams: qp,
                                    audioRef: ({ spawn }) => {
                                        return info.params.main.audio ? spawn(AudioMachineDefinition) : null;
                                    },
                                });
                            },
                        ),
                    },
                    {
                        target: SurveyState.err404,
                        guard: ({
                            event: {
                                output: { code },
                            },
                        }) => code === ApiRespCode.ERR_404,
                    },
                    {
                        target: SurveyState.loaded,
                        guard: ({
                            event: {
                                output: { code },
                            },
                        }) => {
                            return code !== ApiRespCode.ERR_SVR_CODE && code !== ApiRespCode.ERR_AUTH_CODE;
                        },
                        actions: enqueueActions(
                            ({
                                event: {
                                    output: { info, token, startDatetime, agreementText, qp },
                                },
                                enqueue,
                            }) => {
                                const tokenData = parseTokenData(token);

                                setToken(token);

                                enqueue.assign({
                                    agreementText,
                                    loading: ({ context }) => ({ ...context.loading, details: Boolean(info.details) }),
                                    info,
                                    token,
                                    tokenData,
                                    startDatetime,
                                    queryParams: qp,
                                    audioRef: ({ spawn }) => {
                                        return info.params.main.audio ? spawn(AudioMachineDefinition) : null;
                                    },
                                });
                            },
                        ),
                    },
                ],
                onError: {
                    actions: enqueueActions(({ event, enqueue }) => {
                        // @ts-ignore
                        enqueue.assign({ errorText: event?.error?.message });
                    }),
                    target: SurveyState.error,
                },
            },
        },
        [SurveyState.loaded]: {
            always: [
                {
                    target: SurveyState.fetchContent,
                    guard: ({ context }) => {
                        if ((context.info.savingNotComplete && tryToGetTtId()) || context.info.details?.length)
                            return true;
                        const { welcomePage, welcomePageText } = context.info.params.main;
                        if (context.info.params.other.clickReply && !isEmbedded && !isAllPagesOnOnePage) {
                            if (!welcomePageText) return true;
                            return welcomePage === WelcomePageType.NOT;
                        }
                        return (
                            (!withAuth(context) || authenticated(context)) &&
                            welcomePage !== WelcomePageType.TO_SINGLE_PAGE
                        );
                    },
                },
                {
                    target: SurveyState.welcomePage,
                    guard: ({ context }) => {
                        if (context.info.savingNotComplete && tryToGetTtId()) return false;
                        return context.info.params.other.clickReply && !isEmbedded && !isAllPagesOnOnePage
                            ? context.info.params.main.welcomePage !== WelcomePageType.NOT
                            : context.info.params.main.welcomePage === WelcomePageType.TO_SINGLE_PAGE;
                    },
                },
                {
                    target: SurveyState.auth,
                    guard: ({ context }) => withAuth(context) && !authenticated(context),
                },
                {
                    actions: ({ context }) => {
                        const { newToken } = context.info;
                        return assign({ token: newToken });
                    },
                    guard: ({ context }) => !!context.info.newToken,
                },
                {
                    target: SurveyState.completed,
                    guard: ({ context }) => {
                        const { complete, params } = context.info;
                        const { disallowRefilling } = params.other;
                        return complete && disallowRefilling;
                    },
                },
            ],
        },
        [SurveyState.started]: {
            always: [
                {
                    target: SurveyState.fetchContent,
                },
            ],
        },
        [SurveyState.fetchContent]: {
            invoke: {
                src: 'fetchContent',
                input: ({ context }) => ({
                    context,
                }),
                onDone: [
                    {
                        target: SurveyState.bundles,
                        actions: enqueueActions(({ context, enqueue, event }) => {
                            const isAnyQuotaLogic =
                                event.output.hasSurveyQuota ||
                                event.output.questions.some((q) =>
                                    q.logics.some((logic) => logic.type === 4 || logic.type === 5),
                                );
                            if (isAnyQuotaLogic) {
                                enqueue.assign({
                                    quotaRef: ({ spawn, self }) => {
                                        const quotaRef = spawn(QuotaMachineDefinition, {
                                            input: {
                                                parentRef: self,
                                                surveyId: context.info.id,
                                                initialClosedLogicIds: event.output.closedQuotaLogicIds,
                                            },
                                        });
                                        // Бронь квоты опроса уже создана на
                                        // start/response — здесь только запускаем
                                        // keep-alive: heartbeat продлевает TTL,
                                        // пока респондент проходит опрос.
                                        quotaRef.send({ type: 'START_HEARTBEAT' });
                                        console.log('[quota] quota machine spawned, START_HEARTBEAT sent');
                                        return quotaRef;
                                    },
                                });
                                setQuotaSessionActive(context.domain, true);
                            }
                            enqueue.assign({
                                globalLogic: event.output.linkParamsLogic,
                                extendedLogic: event.output.extendedLogic,
                                linkParams: event.output.linkParams,
                            });
                            // Квота уже набрана (проверка на list/page_question):
                            // завершаем прохождение досрочно, текст покажется
                            // по флагу quotaDisqual из stop/response.
                            if (event.output.quotaExhausted) {
                                enqueue.raise({
                                    type: 'QUOTA_EXHAUSTED',
                                    closedLogicIds: event.output.closedQuotaLogicIds,
                                });
                            }
                        }),
                    },
                ],
                onError: SurveyState.error,
            },
        },
        [SurveyState.welcomePage]: {
            on: {
                START: {
                    target: SurveyState.fetchContent,
                },
            },
        },
        [SurveyState.bundles]: {
            entry: [
                assign({
                    pages: ({ event }) => (event as any).output.pages,
                    withRandom: ({ event }) => (event as any).output.withRandom,
                    questions: ({ context, spawn, event, self }) =>
                        (event as any).output.questions.map((q) => {
                            const entity = createQuestion(q, context);

                            return spawn(QuestionMachineDefinition, {
                                systemId: `question-${entity.id}`,
                                input: { ...entity, savedAnswer: q.savedAnswer, parentRef: self },
                            });
                        }),
                }),
                ({ context }) => {
                    fillQuestions(context.questions, context.info.details);
                    hideCarryForwardAnswers(context.questions, (a, value) => {
                        a.getSnapshot().context.hidden = value;
                    });
                },
                enqueueActions(({ context, enqueue }) => {
                    // TODO: унифицировать с CALC_BUNDLES
                    const {
                        bundles,
                        currentIndex = 0,
                        hiddenAnswers,
                    } = prepareBundles(
                        context.domain,
                        context.info,
                        context.pages,
                        context.questions,
                        context.info.details ? false : true,
                        context.info.params.other.clickReply,
                        context.globalLogic,
                        context.queryParams,
                        context.linkParams,
                        context.extendedLogic,
                    );
                    context.questions.forEach((q) => {
                        q.getSnapshot().context.answersActors.forEach((a) => {
                            a.getSnapshot().context.hiddenByAnswersHide =
                                Array.isArray(hiddenAnswers[q.getSnapshot().context.id]) &&
                                hiddenAnswers[q.getSnapshot().context.id].includes(a.getSnapshot().context.id);
                        });
                    });

                    // Таймеры прошлой (брошенной) попытки не должны влиять на
                    // текущий ответ — иначе новый response завершался пустым
                    // сразу после start/response по чужому истёкшему таймеру.
                    clearTimersOfOtherResponse(context.domain, context.info.id, context.tokenData?.rid);

                    const withSt = withSurveyTimer(context, bundles, currentIndex);

                    if (withSt) {
                        const { timerHours, timerMinutes, timerSeconds } = context.info.params.main;

                        const duration = createDuration(timerHours, timerMinutes, timerSeconds).asSeconds();
                        const timerId = `survey_timer-${context.info.id}`;
                        const timer = loadTimer(timerId);
                        if (timer && timer + duration * 1000 < Date.now()) {
                            // Таймер уже истёк — ключ больше не нужен, убираем,
                            // чтобы не мешал при повторном прохождении.
                            removeTimer(timerId);
                            enqueue.raise({
                                type: 'ON_LOAD_SURVEY_TIMER_FINISHED',
                            });
                            enqueue.assign({
                                endDatetime: dayjs(Math.max(+context.startDatetime.toDate(), timer) + duration * 1000),
                            });
                        } else {
                            const now = dayjs();
                            const endDatetime = now.add(duration);
                            const timerId = `survey_timer-${context.info.id}`;
                            if (!context.timerRef) {
                                enqueue.assign({
                                    timerRef: ({ spawn, self }) => {
                                        const timerActor = spawn(TimerMachine, {
                                            systemId: timerId,
                                            input: {
                                                duration,
                                                parentRef: self,
                                                uniqueId: timerId,
                                            },
                                        });
                                        timerActor.send({ type: 'START' });
                                        return timerActor;
                                    },
                                    endDatetime,
                                });
                            } else {
                                enqueue.assign({
                                    endDatetime,
                                });
                            }
                        }
                    }
                    const cb = bundles[currentIndex];
                    const currentBundle = cb ? new BundleEntity(cb, cb?.questions) : null;
                    if (cb) {
                        cb?.questions.forEach((q) => q.send({ type: 'SET_INVALID', value: false }));
                        cb?.questions.forEach((q) => q.send({ type: 'SET_COMMENT_INVALID', value: false }));
                    }
                    const timers = !currentBundle
                        ? []
                        : currentBundle.questions
                              .map((q) => {
                                  const timerId = `question-timer-${q.getSnapshot().context.id}`;
                                  return {
                                      q,
                                      timer: loadTimer(timerId),
                                  };
                              })
                              .filter((s) => Boolean(s.timer));

                    const isAnyOfTimersExpired = timers.some(({ q, timer }) => {
                        const context = q.getSnapshot().context;
                        const duration = partsToSeconds(
                            context.params.hours,
                            context.params.minutes,
                            context.params.seconds,
                        );
                        return timer + duration * 1000 < Date.now();
                    });
                    if (isAnyOfTimersExpired && bundles.length - 1 === currentIndex) {
                        enqueue.raise({
                            type: 'ON_LOAD_SURVEY_TIMER_FINISHED',
                        });
                    }

                    enqueue.assign({
                        bundles,
                        currentBundleIndex: currentIndex,
                    });
                    // Возврат в сохранённое прохождение: перебронируем ранее
                    // занятые квоты, прежде чем показывать бандлы.
                    if (context.quotaRef) {
                        enqueue.raise({ type: 'RESUME_QUOTA_CLAIMS' });
                    }
                    if (bundles.every((b) => b.type !== BundleType.QUESTIONS)) {
                        enqueue.raise({ type: 'SELECT_NEXT_BUNDLE', noValidate: false });
                    }
                }),
            ],
            on: {
                ON_LOAD_SURVEY_TIMER_FINISHED: {
                    target: SurveyState.phoneAuth,
                    actions: assign({
                        stopReason: BundleFinishType.STOP_BY_TIMER,
                    }),
                },
                // Квота набрана, пока респондент внутри (heartbeat/повторная
                // проверка). Для ANSWERS_HIDE завершать нельзя: откатываем
                // респондента к вопросу, на котором закрылся его вариант,
                // сбрасываем хвост и просим выбрать другой ответ. Для остальных
                // квот (в т.ч. квоты опроса) завершаем досрочно: на stop бэк
                // вернёт quotaDisqual, и ответ не попадёт в квоту.
                QUOTA_EXHAUSTED: [
                    {
                        guard: ({ context, event }) =>
                            Boolean(findQuotaHideRollback(context.bundles, event.closedLogicIds)),
                        actions: enqueueActions(({ context, event, enqueue }) => {
                            const target = findQuotaHideRollback(context.bundles, event.closedLogicIds);

                            if (!target) {
                                return;
                            }
                            console.log('[quota] QUOTA_EXHAUSTED → rollback to question', target);

                            // Сбрасываем ответ на целевом вопросе и весь хвост,
                            // чтобы респондент заново выбрал ответ.
                            resetTailQuestions(context.bundles, target.bundleIndex);
                            saveIncompleteAnswersOfSurvey(context);
                            // Скрываем закрывшийся вариант и подсвечиваем
                            // ошибку с текстом «выберите другой ответ».
                            hideQuotaAnswers(context.questions, target.logicId);
                            context.questions
                                .find((q) => q.getSnapshot().context.id === target.questionId)
                                ?.send({ type: 'SET_QUOTA_ERROR', value: true });

                            enqueue.assign({
                                currentBundleIndex: target.bundleIndex,
                                quotaClaimSignatures: {},
                                quotaPendingSignature: null,
                                loading: { ...context.loading, quotas: false },
                            });
                            enqueue.raise({ type: 'CALC_BUNDLES' });
                        }),
                    },
                    {
                        // Закрыта ANSWERS_HIDE-квота, но выбранного (и уже
                        // откаченного) варианта нет: не завершаем — вариант
                        // скрыт, респондент может выбрать другой. Так повторные
                        // heartbeat, пока не сделан новый claim, не выкидывают
                        // из прохождения. Подписи claim сбрасываем: следующий
                        // «Далее» с прежним ответом должен перепроверить квоту.
                        guard: ({ context, event }) => hasQuotaHideLogic(context.questions, event.closedLogicIds),
                        actions: assign({
                            quotaClaimSignatures: {},
                        }),
                    },
                    {
                        target: SurveyState.finishing,
                        actions: assign({
                            stopReason: BundleFinishType.STOP,
                        }),
                    },
                ],
                QUESTION_TOUCHED: [
                    {
                        actions: enqueueActions(({ enqueue, context, event }) => {
                            enqueue.raise({ type: 'HIDE_CARRY_FORWARD_ANSWERS' });
                            enqueue.raise({ type: 'CALC_BUNDLES' });
                            const sourceQuestion = context.questions.find(
                                (q) => q.getSnapshot().context.id === event.questionId,
                            );
                            enqueue.raise({ type: 'SAVE_INCOMPLETE', sourceQuestion });
                            enqueue.assign({
                                progress: ({ context }) => calcSurveyProgress(context.questions),
                                invalidQuestionsQuantity: ({ context }) =>
                                    context.questions.reduce(
                                        (acc, questionRef) => acc + (questionRef.getSnapshot().context.invalid ? 1 : 0),
                                        0,
                                    ),
                                questionAnswers: ({ context }) => getPipingAnswers(context),
                            });
                        }),
                    },
                ],
                SELECT_NEXT_BUNDLE: [
                    {
                        // Квотное бронирование блокирует переход: пока сервис
                        // квот не подтвердит слот, дальше не идём. Внутренний
                        // переход (без target) — остаёмся в текущем состоянии.
                        guard: ({ context }) => {
                            if (!context.quotaRef) {
                                return false;
                            }

                            const cb = selectCurrentBundle(context);
                            const { claims, signature } = collectBundleQuotaClaims(cb);
                            console.log('[quota] SELECT_NEXT_BUNDLE guard', {
                                bundleIndex: context.currentBundleIndex,
                                claims,
                                signature,
                                recorded: context.quotaClaimSignatures[context.currentBundleIndex],
                                loadingQuotas: context.loading.quotas,
                                quotaState: context.quotaRef.getSnapshot().value,
                            });
                            if (claims.length === 0) {
                                return false;
                            }

                            return context.quotaClaimSignatures[context.currentBundleIndex] !== signature;
                        },
                        actions: enqueueActions(({ enqueue }) => {
                            enqueue.raise({ type: 'REQUEST_QUOTA_CLAIM' });
                        }),
                    },
                    {
                        guard: ({ context, event: { noValidate } }) => {
                            const cb = selectCurrentBundle(context);
                            const currentBundle = new BundleEntity(cb, cb?.questions || []);

                            if (noValidate) {
                                return false;
                            } else {
                                currentBundle.validate();

                                if (currentBundle.withTestAnswerReacting) {
                                    return true;
                                }

                                if (currentBundle.isValid) {
                                    return false;
                                } else {
                                    return true;
                                }
                            }
                        },
                        actions: enqueueActions(({ enqueue, context, event: { noValidate } }) => {
                            const cb = selectCurrentBundle(context);
                            const currentBundle = new BundleEntity(cb, cb?.questions || []);

                            if (noValidate) {
                                currentBundle.stopTimers();
                            } else {
                                currentBundle.validate();

                                if (currentBundle.isValid) {
                                    currentBundle.stopTimers();
                                    enqueue.assign({
                                        scrollToQuestionId: -1,
                                    });
                                } else {
                                    enqueue.assign({
                                        scrollToQuestionId: selectFirstInvalidId(context),
                                    });
                                    return false;
                                }
                            }
                        }),
                    },
                    {
                        actions: assign(({ context, self, spawn }) => {
                            const cb = selectCurrentBundle(context);
                            if (!cb) {
                                return {
                                    currentBundleIndex: context.currentBundleIndex,
                                    scrollToQuestionId: -1,
                                };
                            }
                            const currentBundle = new BundleEntity(cb, cb?.questions);
                            cb?.questions.forEach((q) => q.send({ type: 'STOP_TIMER' }));

                            setTimeout(() => scrollSurveyToTop(), 100);
                            notifyPageChanged(context.currentBundleIndex + 1);

                            const { timerOn } = context.info.params.main;

                            if (timerOn) {
                                const isStartTimer = currentBundle.questions.find((q) => {
                                    return String(timerOn) === String(q.getSnapshot().context.id);
                                });

                                if (isStartTimer) {
                                    const { timerHours, timerMinutes, timerSeconds } = context.info.params.main;
                                    const duration = createDuration(timerHours, timerMinutes, timerSeconds).asSeconds();
                                    const timerId = `survey_timer-${context.info.id}`;

                                    if (!context.timerRef) {
                                        const timerActor = spawn(TimerMachine, {
                                            systemId: timerId,
                                            input: {
                                                duration,
                                                parentRef: self,
                                                uniqueId: timerId,
                                            },
                                        });
                                        timerActor.send({ type: 'START' });
                                        return {
                                            timerRef: timerActor,
                                            scrollToQuestionId: -1,
                                            currentBundleIndex: context.currentBundleIndex + 1,
                                        };
                                    } else {
                                        return {
                                            scrollToQuestionId: -1,
                                            currentBundleIndex: context.currentBundleIndex + 1,
                                        };
                                    }
                                }
                            }

                            return {
                                currentBundleIndex: context.currentBundleIndex + 1,
                                scrollToQuestionId: -1,
                            };
                        }),
                        guard: ({ context, event }) => {
                            const nextBundle = selectNextBundle(context);
                            if (!nextBundle || nextBundle.type !== BundleType.QUESTIONS) {
                                return false;
                            }
                            const { noValidate } = event;
                            const cb = selectCurrentBundle(context);
                            const currentBundle = new BundleEntity(cb, cb?.questions);
                            if (noValidate) {
                                return true;
                            } else {
                                currentBundle.validate();
                                if (currentBundle.isValid) {
                                    return true;
                                } else {
                                    return false;
                                }
                            }
                        },
                    },
                    {
                        target: SurveyState.phoneAuth,
                        guard: ({ context, event }) => {
                            const { noValidate } = event;
                            const cb = selectCurrentBundle(context);
                            const currentBundle = new BundleEntity(cb, cb?.questions);
                            const nextBundle = selectNextBundle(context);
                            if (!noValidate) {
                                currentBundle.validate();
                                if (!currentBundle.isValid) {
                                    return false;
                                }
                            }

                            if (!nextBundle || nextBundle.type !== BundleType.QUESTIONS) {
                                return true;
                            }
                        },
                        actions: assign({
                            stopReason: ({ context }) => {
                                const nextBundle = selectNextBundle(context);
                                if (nextBundle) {
                                    if (nextBundle.finishType === BundleFinishType.DISQUAL) {
                                        return BundleFinishType.DISQUAL;
                                    }
                                    if (nextBundle.text) {
                                        return BundleFinishType.TEXT;
                                    }
                                    return BundleFinishType.STOP;
                                }
                                return BundleFinishType.STOP;
                            },
                        }),
                    },
                ],
                SELECT_PREV_BUNDLE: [
                    {
                        actions: assign(({ context }) => {
                            // FIXME: SCROLL RESET
                            // TODO return promise or move to useLayoutEffect
                            setTimeout(() => scrollSurveyToTop(), 100);
                            const cb = selectCurrentBundle(context);
                            if (cb) {
                                const currentBundle = new BundleEntity(cb, cb?.questions);
                                currentBundle.stopTimers();
                            }
                            // Бандлы с истёкшим таймером вопроса пропускаем: ответить
                            // на них уже нельзя, возвращаться туда бессмысленно.
                            // Если подходящего бандла нет — остаёмся на текущем
                            // (кнопка «назад» в этом случае скрыта селектором).
                            let prevIndex = context.currentBundleIndex;
                            for (let i = context.currentBundleIndex - 1; i >= 0; i--) {
                                const bundle = context.bundles[i];
                                if (bundle && !bundle.questions.some(isQuestionTimerExpired)) {
                                    prevIndex = i;
                                    break;
                                }
                            }
                            if (prevIndex !== context.currentBundleIndex) {
                                notifyPageChanged(prevIndex);
                            }
                            return {
                                currentBundleIndex: prevIndex,
                            };
                        }),
                    },
                ],
                TIMER_EXPIRED: [
                    {
                        guard: () => !isPreview,
                        target: SurveyState.phoneAuth,
                        actions: assign(() => {
                            return {
                                stopReason: BundleFinishType.STOP_BY_TIMER,
                            };
                        }),
                    },
                ],
            },
            states: {},
        },
        [SurveyState.phoneAuth]: {
            always: {
                guard: ({ context }) => {
                    return !context.info.params.main.phoneAuth || isPreview;
                },
                target: SurveyState.finishing,
            },
            entry: assign({
                showConfirmSms: ({ context }) =>
                    !context.info.params.main.phoneAuth || isPreview ? 'initial' : 'shown',
            }),
            on: {
                SELECT_NEXT_BUNDLE: {
                    actions: assign({
                        showConfirmSms: 'shown',
                    }),
                },
                PASS_CONFIRM: {
                    actions: assign({
                        showConfirmSms: 'passed',
                    }),
                    target: SurveyState.finishing,
                },
            },
        },
        [SurveyState.updating]: {},
        [SurveyState.blocked]: {},
        [SurveyState.completed]: {
            entry: 'stopQuotaSession',
        },
        [SurveyState.disabled]: {},
        [SurveyState.retryNotAllowed]: {},
        [SurveyState.auth]: {
            on: {
                AUTH_CHECK_VK: {
                    target: SurveyState.authCheckVk,
                },
                AUTH_CHECK_PWD: {
                    target: SurveyState.authCheckPwd,
                },
            },
        },
        [SurveyState.authCheckVk]: {
            invoke: {
                src: fromPromise<{ token: string }, { authdata: string; domain: string; token: string }>(
                    ({ input }) => {
                        return checkVkAuth(input.authdata, input.domain, input.token);
                    },
                ),
                input: ({ event }) => {
                    assertEventType(event, 'AUTH_CHECK_VK');
                    return {
                        authdata: event.authdata,
                        domain: event.domain,
                        token: event.token,
                    };
                },
                onDone: [
                    {
                        target: SurveyState.welcomePage,
                        guard: ({ context }) => context.info.params.main.welcomePage === WelcomePageType.TO_SINGLE_PAGE,
                        actions: assign(({ event }) => {
                            const output = event.output as { token: string };
                            setToken(output.token);
                            return {
                                token: output.token,
                            };
                        }),
                    },
                    {
                        target: SurveyState.started,
                        actions: assign(({ event }) => {
                            const output = event.output as { token: string };
                            setToken(output.token);
                            return {
                                token: output.token,
                            };
                        }),
                    },
                ],
                onError: {
                    target: SurveyState.error,
                },
            },
        },
        [SurveyState.authCheckPwd]: {
            invoke: {
                src: 'checkPwdAuth',
                input: ({ context, event }) => {
                    const { individualPasswords } = context.info.params.main;
                    assertEventType(event, 'AUTH_CHECK_PWD');
                    return {
                        individualPasswords,
                        password: event.password,
                    };
                },
                onDone: [
                    {
                        target: SurveyState.welcomePage,
                        guard: ({
                            context,
                            event: {
                                output: {
                                    token: { code },
                                },
                            },
                        }) =>
                            code === ApiRespCode.OK_CODE &&
                            context.info.params.main.welcomePage === WelcomePageType.TO_SINGLE_PAGE,
                        actions: assign(
                            ({
                                event: {
                                    output: {
                                        token: { token },
                                        password,
                                    },
                                },
                                context,
                            }) => {
                                setToken(token);
                                if (context.info.params.main.individualPasswords && !isPreview) {
                                    setTtId(`${context.info.id}-${password.replace(/[{}()/\\@:]{1}/gi, '_')}`);
                                }

                                return { token, tokenData: parseTokenData(token) };
                            },
                        ),
                    },
                    {
                        target: SurveyState.started,
                        guard: ({
                            event: {
                                output: {
                                    token: { code },
                                },
                            },
                        }) => {
                            return code === ApiRespCode.OK_CODE;
                        },
                        actions: assign(
                            ({
                                event: {
                                    output: {
                                        token: { token },
                                        password,
                                    },
                                },
                                context,
                            }) => {
                                setToken(token);
                                if (context.info.params.main.individualPasswords && !isPreview) {
                                    setTtId(`${context.info.id}-${password.replace(/[{}()/\\@:]{1}/gi, '_')}`);
                                }

                                return { token, tokenData: parseTokenData(token) };
                            },
                        ),
                    },
                    {
                        target: SurveyState.auth,
                        guard: ({
                            event: {
                                output: {
                                    token: { code },
                                },
                            },
                        }) => {
                            return code === ApiRespCode.ERR_REFILL_CODE;
                        },
                        actions: assign({ pwdCheck: CheckPasswordStatus.PASSWORD_ALREADY_USED }),
                    },
                    {
                        target: SurveyState.auth,
                        guard: ({
                            event: {
                                output: {
                                    token: { code },
                                },
                            },
                        }) => code !== ApiRespCode.ERR_REFILL_CODE && code !== ApiRespCode.OK_CODE,
                        actions: assign(({ context }) => {
                            const { domain, individualTries, info } = context;
                            const { individualPasswords } = info.params.main;

                            return {
                                pwdCheck: CheckPasswordStatus.ERROR,
                                individualTries: individualPasswords ? incIndividualTries(domain) : individualTries,
                            };
                        }),
                    },
                ],
            },
        },
        [SurveyState.err404]: {},
        [SurveyState.error]: {},
        [SurveyState.finishing]: {
            invoke: {
                src: 'finishSurvey',
                input: ({ context }) => ({ context }),
                onDone: [
                    {
                        target: SurveyState.results,
                        guard: isFinishTypeResults,
                        actions: assign({
                            quotaDisqual: ({ event }) => Boolean(event.output.quotaDisqual),
                        }),
                    },
                    {
                        target: SurveyState.testResults,
                        guard: ({ event }) => {
                            return Boolean(event.output.testScore);
                        },
                        actions: [
                            assign({
                                quotaDisqual: ({ event }) => Boolean(event.output.quotaDisqual),
                            }),
                            assign(({ event }) => {
                                return {
                                    promocode: event.output.promo_code || '',
                                    testScore: event.output.testScore,
                                };
                            }),
                            enqueueActions(
                                ({
                                    context,
                                    event: {
                                        output: { testScore },
                                    },
                                }) => {
                                    if (!testScore) {
                                        return;
                                    }
                                    const testQuestions = context.questions.filter((q) =>
                                        TypesWithScore.has(q.getSnapshot().context.type),
                                    );

                                    // testQuestions.forEach((testQ) => {
                                    //     const {
                                    //         params: { hours, minutes, seconds, timer },
                                    //         // setFactTime,
                                    //         // setTargetTime,
                                    //         // timerAgo,
                                    //         timer: timerRef,
                                    //     } = testQ.getSnapshot().context;
                                    //     if (timer) {
                                    //         // timerRef.getSnapshot().context.
                                    //         // setFactTime(timerAgo);
                                    //         const targetTime = dayjs.duration(
                                    //             hours * 3600 + minutes * 60 + seconds,
                                    //             'seconds',
                                    //         );
                                    //         // setTargetTime(targetTime);
                                    //     }
                                    // });

                                    const questionsById: Record<string, QuestionActorRef> = testQuestions.reduce(
                                        (acc, curr) => {
                                            acc[curr.getSnapshot().context.id] = curr;
                                            return acc;
                                        },
                                        {},
                                    );

                                    Object.entries(testScore.questions).forEach(
                                        ([id, { answered, answers, maxScore, score }]: [
                                            string,
                                            (typeof testScore.questions)[number],
                                        ]) => {
                                            const q = questionsById[id];
                                            if (!q) return;

                                            q.send({
                                                type: 'SET_TEST_RESULT',
                                                currentPoints: score,
                                                maxPoints: maxScore,
                                                isChecked: answered,
                                            });
                                            const questionAnswers: Record<string, QuestionAnswerActor> = q
                                                .getSnapshot()
                                                .context.answersActors.reduce((acc, a) => {
                                                    acc[a.getSnapshot().context.id] = a;
                                                    return acc;
                                                }, {});

                                            Object.entries(answers).forEach(
                                                ([id, { answered, correct }]: [string, TestScoreAnswer]) => {
                                                    const a = questionAnswers[id];

                                                    if (correct) {
                                                        a.send({
                                                            type: 'SET_CHECK_STATUS',
                                                            testCheckStatus: TestCheckStatus.VALID,
                                                        });
                                                    }

                                                    if (answered && !correct) {
                                                        a.send({
                                                            type: 'SET_CHECK_STATUS',
                                                            testCheckStatus: TestCheckStatus.INVALID,
                                                        });
                                                    }
                                                },
                                            );
                                        },
                                    );
                                },
                            ),
                        ],
                    },
                    {
                        target: SurveyState.finished,
                        actions: assign(
                            ({
                                context,
                                event: {
                                    output: { promo_code, quotaDisqual },
                                },
                            }) => {
                                const surveyPromocode = getSurveyPromocode();

                                // Fallback на cookie нужен, когда stop/response
                                // повторили уже завершённый ответ и бэкенд не
                                // вернул промокод (старые версии/сетевой сбой).
                                if (
                                    context.info?.params?.main?.completePageText?.includes('{promocode}') &&
                                    !promo_code &&
                                    !surveyPromocode
                                ) {
                                    // Штатная ситуация (промокоды не настроены):
                                    // warn, чтобы не сорить в Sentry; в тексте {promocode}
                                    // подменяется на '' (см. assign ниже и selectors).
                                    console.warn('no promocode found');
                                }
                                return {
                                    currentBundleIndex: (() => {
                                        const nextBundle = selectNextBundle(context);
                                        if (nextBundle) {
                                            return context.currentBundleIndex + 1;
                                        }
                                        return context.currentBundleIndex;
                                    })(),
                                    promocode: promo_code || surveyPromocode || '',
                                    quotaDisqual: Boolean(quotaDisqual),
                                };
                            },
                        ),
                    },
                ],
                onError: [
                    {
                        guard: (args) => {
                            return (
                                typeof args.event.error === 'object' &&
                                'code' in args.event.error &&
                                typeof args.event.error.code === 'number' &&
                                args.event.error.code === ApiRespCode.ERR_END_SURVEY
                            );
                        },
                        target: SurveyState.disabled,
                        actions: (args) => {
                            if (
                                typeof args.event.error === 'object' &&
                                'code' in args.event.error &&
                                typeof args.event.error.code === 'number' &&
                                finishSurveyExpectedErrors.has(args.event.error.code)
                            ) {
                                return;
                            }
                            if (args?.event?.error) {
                                console.error('finishSurvey onError', args.event.error);
                            }
                        },
                    },
                    {
                        target: SurveyState.error,
                        actions: (args) => {
                            if (
                                typeof args.event.error === 'object' &&
                                'code' in args.event.error &&
                                typeof args.event.error.code === 'number' &&
                                args.event.error.code === ApiRespCode.ERR_ANSWERED_RESPONSE
                            ) {
                                clearTtId();
                            }
                        },
                    },
                ],
            },
            entry: [
                assign({
                    stopDatetime: () => dayjs(),
                }),
            ],
        },
        [SurveyState.finished]: {
            entry: 'stopQuotaSession',
        },
        [SurveyState.results]: {
            entry: 'stopQuotaSession',
        },
        [SurveyState.testResults]: {
            entry: 'stopQuotaSession',
        },
    },
    on: {
        UPDATE_TRANSLATIONS_QUESTIONS_PAGES: {
            actions: enqueueActions(
                ({
                    event: {
                        data: { pages, questions, extendedLogic },
                    },
                    context,
                    enqueue,
                }) => {
                    context.questions.forEach((actor) => {
                        const q = questions.find((q) => q.id === actor.getSnapshot().context.id);
                        if (q) {
                            actor.send({
                                type: 'UPDATE_TRANSLATIONS',
                                question: q as any,
                            });
                        }
                    });

                    enqueue.assign({
                        pages,
                        extendedLogic,
                    });
                    enqueue.raise({ type: 'CALC_BUNDLES' });
                },
            ),
        },
        UPDATE_TRANSLATIONS_INFO: {
            actions: enqueueActions(
                ({
                    event: {
                        data: { info },
                    },
                    enqueue,
                }) => {
                    enqueue.assign({
                        info: ({ context: { info: prevInfo } }) => ({ ...prevInfo, ...info }),
                    });
                },
            ),
        },
        SET_DETAILS_LOADED: {
            actions: [
                assign({
                    loading: ({ context: { loading } }) => ({ ...loading, details: false }),
                }),
            ],
        },
        SET_INFO: {
            actions: assign({
                info: ({ event }) => {
                    return event.info;
                },
            }),
        },
        RELOAD: {
            actions: enqueueActions(({ context }) => {
                context.bootstrapRef.send({
                    type: 'START',
                    domain: context.domain,
                    token: context.token,
                    individualTries: context.individualTries,
                });
            }),
        },
        RESUME_QUOTA_CLAIMS: {
            actions: enqueueActions(({ context, enqueue }) => {
                const claims = collectResumeQuotaClaims(context.bundles);

                if (claims.length === 0 || !context.quotaRef) {
                    return;
                }

                const requestId = context.quotaClaimRequestId + 1;

                enqueue.assign({
                    resumeQuotaClaims: claims,
                    resumeQuotaIndex: 0,
                    quotaClaimRequestId: requestId,
                    loading: { ...context.loading, quotas: true },
                });
                context.quotaRef.send({ type: 'CLAIM', request: { claims: [claims[0]], requestId } });
            }),
        },
        REQUEST_QUOTA_CLAIM: {
            actions: enqueueActions(({ context, enqueue }) => {
                const cb = selectCurrentBundle(context);
                const { claims, signature } = collectBundleQuotaClaims(cb);
                const requestId = context.quotaClaimRequestId + 1;

                console.log('[quota] REQUEST_QUOTA_CLAIM', {
                    requestId,
                    bundleIndex: context.currentBundleIndex,
                    claims,
                    signature,
                    hasQuotaRef: Boolean(context.quotaRef),
                    quotaState: context.quotaRef?.getSnapshot().value,
                });

                enqueue.assign({
                    quotaClaimRequestId: requestId,
                    quotaPendingSignature: signature,
                    loading: { ...context.loading, quotas: true },
                });

                if (context.quotaRef) {
                    context.quotaRef.send({ type: 'CLAIM', request: { claims, requestId } });
                } else {
                    console.warn('[quota] REQUEST_QUOTA_CLAIM without quotaRef — claim not sent, loader will hang');
                }
            }),
        },
        QUOTA_CLAIM_OUTCOME: {
            actions: enqueueActions(({ context, event, enqueue }) => {
                const { outcome } = event;
                const signature = context.quotaPendingSignature ?? '';
                // Действие квоты определяем по логике вопроса: в denied.action
                // сервис может вернуть устаревший transition_type (например, 2
                // вместо ANSWERS_HIDE), и отказ пройдёт как «не скрытие».
                const deniedLogic = outcome.denied
                    ? findQuotaLogicById(context.questions, outcome.denied.logicId)
                    : null;
                const isHideAction =
                    !outcome.allowed &&
                    (deniedLogic
                        ? isQuotaHideLogic(deniedLogic)
                        : outcome.denied?.action === LogicTransition.ANSWERS_HIDE);
                const isResumeActive = context.resumeQuotaIndex < context.resumeQuotaClaims.length;

                console.log('[quota] QUOTA_CLAIM_OUTCOME', {
                    outcome,
                    signature,
                    isHideAction,
                    isResumeActive,
                    bundleIndex: context.currentBundleIndex,
                });

                if (isResumeActive && outcome.allowed) {
                    const nextIndex = context.resumeQuotaIndex + 1;

                    if (nextIndex < context.resumeQuotaClaims.length && context.quotaRef) {
                        const requestId = context.quotaClaimRequestId + 1;
                        enqueue.assign({ resumeQuotaIndex: nextIndex, quotaClaimRequestId: requestId });
                        context.quotaRef.send({
                            type: 'CLAIM',
                            request: { claims: [context.resumeQuotaClaims[nextIndex]], requestId },
                        });
                        return;
                    }

                    // Все ранее занятые квоты перебронированы — продолжаем
                    // с сохранённого места без отката.
                    enqueue.assign({
                        resumeQuotaClaims: [],
                        resumeQuotaIndex: 0,
                        loading: { ...context.loading, quotas: false },
                    });
                    return;
                }

                if (isResumeActive) {
                    // Возврат и отказ: откатываем на первый недоступный
                    // квотный вопрос и стираем хвост ответов. Сервис квот уже
                    // проставил закрытую квоту в quotaRef.
                    const targetQuestionId = outcome.denied?.questionId ?? null;
                    const targetIndex = context.bundles.findIndex((bundle) =>
                        bundle.questions.some((q) => q.getSnapshot().context.id === targetQuestionId),
                    );

                    if (targetIndex === -1 || !context.quotaRef || !isHideAction) {
                        // Нечего/некуда откатывать, либо квота с действием
                        // дисквала/завершения — применяем действие квоты.
                        enqueue.assign({
                            resumeQuotaClaims: [],
                            resumeQuotaIndex: 0,
                            loading: { ...context.loading, quotas: false },
                        });
                        enqueue.raise({ type: 'CALC_BUNDLES' });
                        enqueue.raise({ type: 'SELECT_NEXT_BUNDLE', noValidate: false });
                        return;
                    }

                    resetTailQuestions(context.bundles, targetIndex);
                    // Хвост больше не dirty — сохраняем урезанную незавершёнку.
                    saveIncompleteAnswersOfSurvey(context);
                    if (outcome.denied) {
                        hideQuotaAnswers(context.questions, outcome.denied.logicId);
                    }

                    enqueue.assign({
                        resumeQuotaClaims: [],
                        resumeQuotaIndex: 0,
                        quotaClaimSignatures: {},
                        quotaPendingSignature: null,
                        loading: { ...context.loading, quotas: false },
                        currentBundleIndex: targetIndex,
                    });
                    enqueue.raise({ type: 'CALC_BUNDLES' });
                    return;
                }

                // При ANSWERS_HIDE подпись не запоминаем: вариант закрыт, и
                // повторный переход с ним должен снова спросить сервис квот,
                // а не проскочить мимо бронирования.
                enqueue.assign({
                    loading: { ...context.loading, quotas: false },
                    quotaPendingSignature: null,
                    quotaClaimSignatures: isHideAction
                        ? context.quotaClaimSignatures
                        : {
                              ...context.quotaClaimSignatures,
                              [context.currentBundleIndex]: signature,
                          },
                });

                if (!outcome.allowed) {
                    if (isHideAction && context.quotaRef) {
                        const hidden = outcome.denied
                            ? hideQuotaAnswers(context.questions, outcome.denied.logicId)
                            : false;
                        // Вариант закрылся по квоте: подсвечиваем ошибку на вопросе,
                        // чтобы респондент понял, почему выбор сбросился.
                        const deniedQuestionId = outcome.denied?.questionId;
                        context.questions
                            .find((q) => q.getSnapshot().context.id === deniedQuestionId)
                            ?.send({ type: 'SET_QUOTA_ERROR', value: true });
                        console.log('[quota] ANSWERS_HIDE applied', { hidden, denied: outcome.denied });
                        if (hidden) {
                            enqueue.raise({ type: 'CALC_BUNDLES' });
                        }
                        // Скрытие варианта не означает отказ от прохождения:
                        // остаёмся на странице, респондент выбирает другой ответ.
                        return;
                    }

                    // Дисквал/завершение/индивидуальное завершение: статус уже
                    // проставлен сервисом в quotaRef, логика бандлов применит
                    // действие квоты при пересборке.
                    enqueue.raise({ type: 'CALC_BUNDLES' });
                }

                enqueue.raise({ type: 'SELECT_NEXT_BUNDLE', noValidate: false });
            }),
        },
        QUOTA_EXPIRED: {
            actions: enqueueActions(({ context, enqueue }) => {
                console.warn('[quota] QUOTA_EXPIRED', { savingNotComplete: context.info.savingNotComplete });
                // Бронь протухла (нет связи дольше TTL). Чтобы не пустить
                // респондента дальше без слота, перезапускаем bootstrap:
                // сервис квот перебронирует слоты при возврате.
                // Живых броней больше нет — release при уходе не нужен.
                setQuotaSessionActive(context.domain, false);
                enqueue.assign({
                    loading: { ...context.loading, quotas: false },
                });
                if (context.info.savingNotComplete) {
                    enqueue.raise({ type: 'RELOAD' });
                }
            }),
        },
        // Heartbeat принёс свежий снапшот закрытых квот: синхронизируем
        // скрытие/показ вариантов (при овербукинге брони истекают, и варианты
        // должны возвращаться в UI).
        QUOTA_HIDES_UPDATED: {
            actions: enqueueActions(({ context, event, enqueue }) => {
                const changed = syncQuotaHides(
                    context.questions,
                    event.closedAnswers.map((item) => item.logicId),
                );
                if (changed) {
                    // Статус квот изменился (закрылась/открылась): сбрасываем
                    // запомненные подписи claim, чтобы «Далее» с тем же ответом
                    // заново сходил в сервис квот, а не проскочил по старой брони.
                    enqueue.assign({ quotaClaimSignatures: {} });
                    enqueue.raise({ type: 'CALC_BUNDLES' });
                }
            }),
        },
        SCROLL_RESET: {
            actions: assign({
                scrollToQuestionId: -1,
            }),
        },
        SAVE_INCOMPLETE: {
            guard: ({ context }) => {
                const saveIncomplete = selectSaveIncomplete(context);
                return saveIncomplete;
            },
            actions: ({ context, event: { sourceQuestion } }) => {
                if (isPreview) return;
                saveIncompleteAnswersOfSurvey(context);
                addDetails(mapQuestionForQueue(sourceQuestion));
            },
        },
        HIDE_CARRY_FORWARD_ANSWERS: {
            actions: enqueueActions(({ context }) => {
                hideCarryForwardAnswers(context.questions, (a, value) => {
                    a.send({
                        type: 'SET_HIDDEN',
                        value,
                    });
                });
            }),
        },
        QUESTION_SHOWN: {
            actions: enqueueActions(({ context, enqueue, event }) => {
                enqueue.raise({ type: 'CALC_BUNDLES' });

                const question = context.questions.find((q) => q.getSnapshot().context.id === event.questionId);

                if (question?.getSnapshot().context.type === QuestionType.AB_TESTING) {
                    // AB-вопрос показан: сразу фиксируем показанный вариант (как
                    // при финише). Дальше он сам попадает в сохранение
                    // незавершёнки: CHANGE ответа -> QUESTION_RESPONSE_CHANGE
                    // -> dirty + QUESTION_TOUCHED -> SAVE_INCOMPLETE (#683).
                    fillAbTestingQuestions([question]);
                }
            }),
        },
        CALC_BUNDLES: {
            actions: enqueueActions(({ context, enqueue }) => {
                const { bundles, hiddenAnswers } = prepareBundles(
                    context.domain,
                    context.info,
                    context.pages,
                    context.questions,
                    false,
                    context.info.params.other.clickReply && !isAllPagesOnOnePage,
                    context.globalLogic,
                    context.queryParams,
                    context.linkParams,
                    context.extendedLogic,
                );
                context.questions.forEach((q) => {
                    q.getSnapshot().context.answersActors.forEach((a) => {
                        a.getSnapshot().context.hiddenByAnswersHide =
                            Array.isArray(hiddenAnswers[q.getSnapshot().context.id]) &&
                            hiddenAnswers[q.getSnapshot().context.id].includes(a.getSnapshot().context.id);
                    });
                });
                enqueue.assign({ bundles });
            }),
        },
    },
});

export type SurveyMachineActor = ActorRefFrom<typeof surveyMachine>;
